import { z } from "zod";
import {
  CertificationSchema,
  ContactSchema,
  EducationSchema,
  WorkExperienceSchema,
} from "./resume.js";

/**
 * The Verified CV (ARCHITECTURE §5.3, §7.7, REQ-2.7). `sections` is a snapshot
 * composed from a parsed resume + the user's matched skill claims. Each skill
 * carries a `verified` flag and, when verified, the `proofPublicId` linking to
 * its credential's public verify page (REQ-2.9). Claimed-vs-verified is the
 * core distinction the builder and hosted page render.
 */

export const CvSkillSchema = z.object({
  label: z.string(),
  verified: z.boolean().default(false),
  /** Public id of the proving credential → `/id/:publicId` (verified only). */
  proofPublicId: z.string().nullable().default(null),
});

export const CvSectionsSchema = z.object({
  /** Display name + headline shown at the top of the CV. */
  name: z.string().nullable().default(null),
  headline: z.string().nullable().default(null),
  contact: ContactSchema.default({}),
  summary: z.string().nullable().default(null),
  work: z.array(WorkExperienceSchema).default([]),
  education: z.array(EducationSchema).default([]),
  certifications: z.array(CertificationSchema).default([]),
  skills: z.array(CvSkillSchema).default([]),
});

export type CvSkill = z.infer<typeof CvSkillSchema>;
export type CvSections = z.infer<typeof CvSectionsSchema>;

/** Slug rules for hosted CV URLs (`/cv/:slug`): lowercase, url-safe, 3–48 chars. */
export const CvSlugSchema = z
  .string()
  .min(3)
  .max(48)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, "lowercase letters, numbers, and hyphens only");

/** One queue message = one CV to render to PDF via Browser Rendering (REQ-2.10). */
export interface PdfRenderMessage {
  cvId: string;
  slug: string;
  userId: string;
}
